import { ArrowSquareOut as ExternalLink, Warning as AlertTriangle, ArrowsClockwise as RefreshCw } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)", green: "var(--pr-green)", amber: "#C9861B" };

const timeAgo = (iso) => {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(mins) || mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

/** Sheet sync health: how fresh your Google Sheet is, and a one-tap reconnect when it stalls. */
export const SheetSyncBadge = ({ sync, sheetUrl, sheetError }) => {
  if (!sync?.connected) {
    return (
      <p className="text-[11px] text-center" style={{ color: COLOR.muted }} data-testid="caddy-sync-status">
        Logging in app-only mode{sheetError ? `, ${sheetError}` : ". Your wins are saved here"}
      </p>
    );
  }

  const url = sync.sheet_url || sheetUrl;
  const when = timeAgo(sync.last_synced_at);

  // Sheet answered but today isn't on its relay calendar yet (race hasn't started / just ended).
  // That is NOT a sync problem. Never send people to reconnect for it.
  if (sync.healthy && sync.in_calendar === false) {
    return (
      <p className="text-[11px] text-center" style={{ color: COLOR.muted }} data-testid="caddy-sync-status">
        <span data-testid="sheet-sync-badge" style={{ color: COLOR.green, fontWeight: 700 }}>Sheet connected ✓</span>
        {" "}, today isn't on your sheet's relay calendar, so wins are saved in the app until your race is running.
        {url && (
          <>
            {" · "}
            <a href={url} target="_blank" rel="noreferrer" className="underline">open sheet</a>
          </>
        )}
      </p>
    );
  }

  if (sync.healthy || (!sync.needs_reconnect && !sync.error)) {
    return (
      <p
        className="flex items-center justify-center gap-1.5 text-[11px] flex-wrap"
        style={{ color: COLOR.muted }}
        data-testid="caddy-sync-status"
      >
        <span data-testid="sheet-sync-badge" className="inline-flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "var(--pr-green-surface)", boxShadow: `0 0 0 3px rgba(62,142,78,0.18)` }}
          />
          <span style={{ color: COLOR.green, fontWeight: 700 }}>Sheet synced {when}</span>
        </span>
        {url && (
          <>
            <span aria-hidden="true">·</span>
            <a href={url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
              open sheet <ExternalLink className="w-3 h-3" />
            </a>
          </>
        )}
      </p>
    );
  }

  if (!sync.needs_reconnect) {
    return (
      <p className="text-[11px] text-center" style={{ color: COLOR.muted }} data-testid="caddy-sync-status">
        <span data-testid="sheet-sync-badge">Sheet connected · couldn't reach Google just now{when ? ` (last synced ${when})` : ""}. Your wins are saved here and will sync on the next visit.</span>
      </p>
    );
  }

  return (
    <div
      className="rounded-2xl px-4 py-3.5 flex items-start gap-2.5 pr-card-tint"
      style={{ background: "var(--pr-tint-gold)" }}
      data-testid="caddy-sync-status"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: COLOR.amber }} />
      <div className="flex-1 min-w-0" data-testid="sheet-sync-badge">
        <p className="text-[11px] font-bold" style={{ color: COLOR.amber }}>
          Your sheet isn't syncing{when ? `, last synced ${when}` : ""}
        </p>
        <p className="text-[11px] leading-relaxed mb-1.5" style={{ color: COLOR.ink2 }}>
          Everything you log is safe here. Google just needs re-approving to start filling your sheet again.
        </p>
        {sync.reconnect_path && (
          <a
            href={`${API}${sync.reconnect_path}&ret=${encodeURIComponent(
              typeof window !== "undefined" ? window.location.pathname : "/app"
            )}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-4 py-2 rounded-full pr-btn-primary"
            data-testid="sheet-sync-reconnect"
          >
            <RefreshCw className="w-3 h-3" /> Reconnect Google
          </a>
        )}
      </div>
    </div>
  );
};

export default SheetSyncBadge;
